import type { TFunction } from 'i18next';

import { MFA_FACTOR, MFA_RECOVERY_METHOD } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSAccordion, type SDSAccordionItem } from '@repo/sds/SDSAccordion';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';

import { confirmActionState } from './mfa-settings-dialogs-utils';
import { MfaSettingsPage } from './mfa-settings-page';
import { normalizeExistingFactorProof } from './use-mfa-settings';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

function existingFactorCodeLabel(factor: string, t: TFunction<'common'>) {
  if (factor === MFA_RECOVERY_METHOD) {
    return t('security.mfa.recoveryCodeLabel');
  }
  if (factor === MFA_FACTOR.Email) {
    return t('security.mfa.emailCodeLabel');
  }
  if (factor === MFA_FACTOR.Sms) {
    return t('security.mfa.smsCodeLabel');
  }
  return t('security.mfa.totpCodeLabel');
}

function otpResendButton({
  busy,
  cooldownSeconds,
  dataTestId,
  cooldownKey,
  onResend,
  t,
}: {
  busy: boolean;
  cooldownSeconds: number;
  cooldownKey: 'emailCooldown' | 'smsCooldown';
  dataTestId: string;
  onResend: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <SDSButton
      dataTestId={dataTestId}
      isDisabled={busy || cooldownSeconds > 0}
      mode="ghost"
      onClick={onResend}
      text={
        cooldownSeconds > 0 ? t(`security.mfa.${cooldownKey}`, { seconds: cooldownSeconds }) : t('security.mfa.resend')
      }
    />
  );
}

export function MfaExistingFactorDialog({
  actionError,
  busy,
  cooldownSeconds,
  enrolled,
  existingFactor,
  existingFactorCode,
  isMobile,
  onCancel,
  onExistingFactorChange,
  onExistingFactorCodeChange,
  onResendEmail,
  onSubmitExistingFactor,
  t,
}: {
  actionError?: string;
  busy: boolean;
  cooldownSeconds: number;
  enrolled: readonly string[];
  existingFactor: string;
  existingFactorCode: string;
  isMobile: boolean;
  onCancel: () => void;
  onExistingFactorChange: (value: string) => void;
  onExistingFactorCodeChange: (value: string) => void;
  onResendEmail: () => Promise<boolean>;
  onSubmitExistingFactor: () => void;
  t: TFunction<'common'>;
}) {
  const errorText = actionError ? (
    <SDSText color="alert" dataTestId={MFA.ERROR} text={actionError} type="body-small" />
  ) : null;

  const isPasskeyProof = existingFactor === MFA_FACTOR.Passkey;
  const proofFields = isPasskeyProof ? (
    <SDSFlexbox direction="column" gap="text-small">
      <SDSText color="text-secondary" text={t('security.mfa.loginPasskeyDescription')} type="body-medium" />
    </SDSFlexbox>
  ) : (
    <SDSFlexbox direction="column" gap="text-small">
      <SDSTextInput
        dataTestId={MFA.EXISTING_FACTOR_CODE}
        isFullWidth
        label={existingFactorCodeLabel(existingFactor, t)}
        onChange={value => {
          const proof = normalizeExistingFactorProof(existingFactor, value, enrolled);
          onExistingFactorChange(proof.existingFactor);
          onExistingFactorCodeChange(value);
        }}
        value={existingFactorCode}
      />
      {existingFactor === MFA_RECOVERY_METHOD ? (
        <SDSText color="text-secondary" text={t('security.mfa.recoveryCodeHint')} type="body-small" />
      ) : null}
      {existingFactor === MFA_FACTOR.Email
        ? otpResendButton({
            busy,
            cooldownSeconds,
            cooldownKey: 'emailCooldown',
            dataTestId: MFA.EMAIL_RESEND,
            onResend: () => void onResendEmail(),
            t,
          })
        : null}
      {existingFactor === MFA_FACTOR.Sms
        ? otpResendButton({
            busy,
            cooldownSeconds,
            cooldownKey: 'smsCooldown',
            dataTestId: MFA.SMS_RESEND,
            onResend: () => void onResendEmail(),
            t,
          })
        : null}
    </SDSFlexbox>
  );

  const items: SDSAccordionItem[] = [];
  if (enrolled.includes(MFA_FACTOR.Passkey)) {
    items.push({
      content: existingFactor === MFA_FACTOR.Passkey ? proofFields : null,
      id: MFA_FACTOR.Passkey,
      title: t('security.mfa.factorPasskey'),
    });
  }
  if (enrolled.includes(MFA_FACTOR.Totp)) {
    items.push({
      content: existingFactor === MFA_FACTOR.Totp ? proofFields : null,
      id: MFA_FACTOR.Totp,
      title: t('security.mfa.factorTotp'),
    });
  }
  if (enrolled.includes(MFA_FACTOR.Email)) {
    items.push({
      content: existingFactor === MFA_FACTOR.Email ? proofFields : null,
      id: MFA_FACTOR.Email,
      title: t('security.mfa.factorEmail'),
    });
  }
  if (enrolled.includes(MFA_FACTOR.Sms)) {
    items.push({
      content: existingFactor === MFA_FACTOR.Sms ? proofFields : null,
      id: MFA_FACTOR.Sms,
      title: t('security.mfa.factorSms'),
    });
  }
  items.push({
    content: existingFactor === MFA_RECOVERY_METHOD ? proofFields : null,
    id: MFA_RECOVERY_METHOD,
    title: t('security.mfa.factorRecovery'),
  });

  const title = t('security.mfa.reauthTitle');
  const confirmState = confirmActionState(busy, !isPasskeyProof && existingFactorCode.length === 0);
  const factorFields = (
    <SDSFlexbox direction="column" gap="text-medium">
      <SDSText text={t('security.mfa.existingFactorLabel')} type="body-medium" />
      <SDSAccordion
        className="MfaSettingsSection__factorChoices"
        dataTestId={MFA.EXISTING_FACTOR}
        defaultExpanded={existingFactor}
        itemTriggerTestIdPrefix={MFA.EXISTING_FACTOR}
        items={items}
        onValueChange={ids => {
          const next = ids.find(id => id !== existingFactor) ?? ids[0];
          if (next && next !== existingFactor) {
            onExistingFactorChange(next);
            onExistingFactorCodeChange('');
          }
        }}
        value={[existingFactor]}
      />
      {errorText}
    </SDSFlexbox>
  );

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          <>
            <SDSButton dataTestId={MFA.CANCEL} mode="outline" onClick={onCancel} text={t('security.mfa.cancel')} />
            <SDSButton
              dataTestId={MFA.CONFIRM}
              isDisabled={confirmState.disabled}
              isSubmitting={busy}
              onClick={onSubmitExistingFactor}
              text={isPasskeyProof ? t('security.mfa.loginPasskeyConfirm') : t('security.mfa.confirm')}
            />
          </>
        }
        ariaLabel={title}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.REAUTH_DIALOG}
        onBack={onCancel}
      >
        <SDSText text={title} type="h5" />
        {factorFields}
      </MfaSettingsPage>
    );
  }

  return (
    <SDSDialog
      dataTestId={MFA.REAUTH_DIALOG}
      isOpen
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.CONFIRM,
        ...confirmState,
        label: isPasskeyProof ? t('security.mfa.loginPasskeyConfirm') : t('security.mfa.confirm'),
        onClick: onSubmitExistingFactor,
      }}
      secondaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.cancel'), onClick: onCancel }}
      size="small"
      title={title}
    >
      {factorFields}
    </SDSDialog>
  );
}
